import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Briefcase, MapPin, DollarSign, Bookmark, BookmarkCheck, Sparkles, Search, ExternalLink, Loader2, Lock, ArrowRight,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const JobCard = ({ job, onSave, onUnsave }) => (
    <motion.div
        initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
        data-testid={`match-${job.id}`}
        className="card-soft p-5 flex flex-col gap-4 group hover:border-emerald-500/40 transition-colors"
    >
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
                <h3 className="font-heading font-semibold text-foreground leading-snug">
                    <Link to={`/careers/${job.id}`} className="hover:text-emerald-600 dark:hover:text-emerald-400" target="_blank" rel="noopener noreferrer">
                        {job.title}
                    </Link>
                </h3>
                <div className="text-sm text-muted-foreground mt-1">{job.company}</div>
            </div>
            <div className="text-right shrink-0">
                <div className="mono-score text-2xl font-bold text-emerald-600 dark:text-emerald-400">{job.match_score}%</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">match</div>
            </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><MapPin size={11} /> {job.location}</span>
            {job.salary && <span className="inline-flex items-center gap-1"><DollarSign size={11} /> {job.salary}</span>}
            <Badge variant="outline" className="text-[10px]">{job.employment_type}</Badge>
            {job.industry && <Badge variant="outline" className="text-[10px]">{job.industry}</Badge>}
        </div>
        <div className="mt-auto pt-3 border-t border-border flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
                {job.applied && <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 border text-[10px]">Applied</Badge>}
            </div>
            <div className="flex items-center gap-2">
                <button
                    onClick={() => (job.saved ? onUnsave(job) : onSave(job))}
                    data-testid={`save-${job.id}`}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${job.saved ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-700 dark:text-emerald-400" : "border-border hover:border-brand-navy"}`}
                >
                    {job.saved ? <><BookmarkCheck size={13} /> Saved</> : <><Bookmark size={13} /> Save</>}
                </button>
                <Link
                    to={`/careers/${job.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`open-${job.id}`}
                    className="btn-green text-xs px-3 py-1.5 inline-flex items-center gap-1"
                >
                    View & apply <ExternalLink size={11} />
                </Link>
            </div>
        </div>
    </motion.div>
);

const JobMatches = () => {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [savedOnly, setSavedOnly] = useState(false);
    const [loading, setLoading] = useState(true);
    const [locked, setLocked] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/professional/matches");
            setItems(data);
            setLocked(false);
        } catch (err) {
            // 402 → free-tier user is out of monthly AI match quota. Render a
            // locked upsell card instead of the misleading "No matches yet".
            if (err?.response?.status === 402) {
                setLocked(true);
            } else {
                toast.error(formatApiError(err));
            }
        }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => items.filter((j) => {
        const matchesQ = !q || `${j.title} ${j.company} ${j.location}`.toLowerCase().includes(q.toLowerCase());
        const matchesSaved = !savedOnly || j.saved;
        return matchesQ && matchesSaved;
    }), [items, q, savedOnly]);

    const save = async (job) => {
        try {
            await api.post("/professional/saved-jobs", { job_id: job.id });
            setItems((xs) => xs.map((x) => x.id === job.id ? { ...x, saved: true } : x));
            toast.success("Saved to your list.");
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const unsave = async (job) => {
        try {
            await api.delete(`/professional/saved-jobs/${job.id}`);
            setItems((xs) => xs.map((x) => x.id === job.id ? { ...x, saved: false } : x));
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div data-testid="job-matches-page">
            <div className="mb-6">
                <p className="overline">AI Match™</p>
                <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">Job Matches</h1>
                <p className="text-sm text-muted-foreground mt-1">Personalized by our AI. Higher match = better fit for your Talent Passport.</p>
            </div>

            <div className="mb-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
                <div className="relative">
                    <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, company, location…" data-testid="matches-search" className="pl-10 h-11" />
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setSavedOnly(false)}
                        data-testid="tab-all"
                        className={`px-4 py-2.5 rounded-full text-xs font-semibold border ${!savedOnly ? "bg-brand-navy text-white border-brand-navy" : "bg-card border-border hover:border-brand-navy"}`}
                    >All</button>
                    <button
                        onClick={() => setSavedOnly(true)}
                        data-testid="tab-saved"
                        className={`px-4 py-2.5 rounded-full text-xs font-semibold border ${savedOnly ? "bg-brand-navy text-white border-brand-navy" : "bg-card border-border hover:border-brand-navy"}`}
                    >Saved</button>
                </div>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : locked ? (
                <div data-testid="matches-locked" className="card-soft p-10 text-center max-w-lg mx-auto">
                    <div className="mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br from-violet-500 to-emerald-500 text-white flex items-center justify-center mb-4">
                        <Lock size={22} />
                    </div>
                    <h2 className="font-heading font-semibold text-xl text-foreground">You've used this month's AI job matches</h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Free plan includes 3 AI-powered match runs each month. Upgrade to Talent Pro for unlimited daily matches, priority placement, and full AI career coaching.
                    </p>
                    <Link
                        to="/talent/billing"
                        data-testid="matches-locked-cta"
                        className="btn-primary text-sm inline-flex items-center mt-5"
                    >
                        Upgrade to Talent Pro <ArrowRight size={13} className="ml-1.5" />
                    </Link>
                </div>
            ) : filtered.length === 0 ? (
                <div className="card-soft p-12 text-center">
                    <Briefcase size={22} className="mx-auto text-emerald-500" />
                    <h3 className="mt-3 font-heading font-semibold text-xl text-foreground">
                        {savedOnly ? "No saved jobs yet." : "No matches yet."}
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
                        {savedOnly ? "Tap the bookmark on any match to save it here." : "Complete your Talent Passport so our AI can find better matches — new roles land here weekly."}
                    </p>
                    <Link to="/professional/passport" className="btn-green mt-6 inline-flex text-sm">Improve my Passport</Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filtered.map((j) => <JobCard key={j.id} job={j} onSave={save} onUnsave={unsave} />)}
                </div>
            )}

            <div className="mt-8 card-soft p-4 flex items-start gap-3">
                <Sparkles size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground">
                    Match scores are computed against your Talent Passport skills, target role, and resume content. Better passport = better matches.
                </p>
            </div>
        </div>
    );
};

export default JobMatches;
